import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import React from "react";

import Input from "@v1/components/common/designSystem/Input";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import Popover from "@v1/components/designSystem/popover/Popover";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import { Search, Type } from "lucide-react";
import { getSavedStyleValue } from "replo-runtime/shared/savedStyles";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import SavedStylesEmptyState from "./SavedStylesEmptyState";
import { TextStyleRow } from "./TextSavedStyleGroup";

type TextShopStyleModifierProps = {
  savedStyleValueReference: string | ReploMixedStyleValue;
  onSelectSavedStyle(value: string): void;
  onRemove?(): void;
  popoverSideOffset?: number;
};

const TextShopStyleModifier: React.FC<TextShopStyleModifierProps> = ({
  savedStyleValueReference,
  onSelectSavedStyle,
  onRemove,
  popoverSideOffset,
}) => {
  const { textShopStyles: textSavedStyles } = useShopStyles();
  const textSavedStyleName = !isMixedStyleValue(savedStyleValueReference)
    ? getSavedStyleValue(textSavedStyles, savedStyleValueReference)?.name
    : "Mixed";

  const isTextSavedStyleSelected = Boolean(textSavedStyleName);
  const shouldRenderXButton = onRemove && isTextSavedStyleSelected;

  return (
    <Popover
      shouldPreventDefaultOnInteractOutside={false}
      title="Text Styles"
      content={
        <TextValueIndicatorPopoverContent
          onSelectSavedStyle={onSelectSavedStyle}
        />
      }
      sideOffset={popoverSideOffset}
      triggerAsChild
    >
      <SelectionIndicator
        title={textSavedStyleName}
        startEnhancer={<TextBadge />}
        placeholder="Select..."
        endEnhancer={
          shouldRenderXButton && <FormFieldXButton onClick={onRemove} />
        }
        style={{
          width: "100%",
        }}
      />
    </Popover>
  );
};

const TextValueIndicatorPopoverContent: React.FC<{
  onSelectSavedStyle: (value: string) => void;
}> = ({ onSelectSavedStyle }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const { textShopStyles, designLibrary } = useShopStyles();

  const filteredTextSavedStyles = textShopStyles.filter((style) => {
    return style.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="flex flex-col">
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full justify-between gap-1">
          <Input
            size="sm"
            value={searchTerm}
            startEnhancer={<Search size={14} />}
            endEnhancer={() => {
              return (
                searchTerm?.trim() && (
                  <FormFieldXButton onClick={() => setSearchTerm("")} />
                )
              );
            }}
            placeholder="Search"
            layoutClassName="grow"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex h-56 w-full flex-col">
          {filteredTextSavedStyles?.length > 0 ? (
            <div className="styled-scrollbar flex flex-col gap-1 overflow-y-auto">
              {filteredTextSavedStyles.map((shopStyle) => (
                <TextStyleRow
                  key={shopStyle.id}
                  savedStyle={shopStyle}
                  onClick={() => {
                    return onSelectSavedStyle?.(
                      `library.${designLibrary?.id}.styles.${shopStyle.id}.attributes`,
                    );
                  }}
                  modificationEnabled={false}
                />
              ))}
            </div>
          ) : (
            <SavedStylesEmptyState title="No Styles Found" showCta={false} />
          )}
        </div>
      </div>
    </div>
  );
};

const TextBadge: React.FC = () => {
  return <Badge type="icon" icon={<Type size={12} className="text-muted" />} />;
};

export default TextShopStyleModifier;
